import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { backendService } from "../../services/backend.service";
import type { Recommendation, RecommendationParams } from "../../utils/models";
import { isHeadNurse } from "../../utils/utilFunctions";
import { useAuth } from "../AuthProvider";
import { FEATURE_FLAGS } from "../FeatureFlagsProvider";
import { useHospitalData } from "../HospitalDataProvider";
import { useRequestsContext } from "../RequestsContext";
import { useWeekIndex } from "../WeekIndexProvider";
import { RecommendationsContext } from "./RecommendationsContext";

export const RecommendationsProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { chosenDepartment, hospitalData, showErrorSnackbar } = useHospitalData();
	const { staffRequests } = useRequestsContext();
	const { weekIndex } = useWeekIndex();
	const { userInfo } = useAuth();

	const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
	const endTime: DateTime | undefined = hospitalData.weeks.at(weekIndex)?.weeklyShifts.at(0)?.days.at(-1)?.date;

	const {
		data: fetchedRecommendations,
		isLoading,
		error,
		refetch,
	} = useQuery<Recommendation[], Error>({
		queryKey: ["recommendations"],
		queryFn: async () => {
			const startTime: DateTime | undefined = hospitalData.weeks.at(weekIndex)?.weeklyShifts.at(0)?.days.at(0)?.date;

			if (!startTime || !endTime) {
				throw new Error("Start time and end time are required");
			}

			const params: RecommendationParams = {
				departmentId: chosenDepartment.department_id,
				startDate: startTime.toFormat("yyyy-MM-dd"),
				endDate: endTime.toFormat("yyyy-MM-dd"),
			};
			const { status, data: recommendations } = await backendService.getRecommendations(params);

			if (status >= 400 || !recommendations) throw new Error("Could not fetch recommendations");

			return recommendations;
		},
		enabled: false,
	});
	const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);

	// biome-ignore lint/correctness/useExhaustiveDependencies: should refetch when moving between weeks
	useEffect(() => {
		const shouldFetch =
			isHeadNurse(userInfo.role) &&
			!isBlockRecommendationsV2Enabled &&
			Boolean(hospitalData.weeks.length && hospitalData.nurses.length && chosenDepartment.department_id && endTime && endTime > DateTime.now());

		if (shouldFetch) {
			const timeoutId = setTimeout(() => refetch(), 800);
			return () => clearTimeout(timeoutId);
		}
	}, [userInfo, hospitalData, chosenDepartment, endTime, weekIndex, refetch, staffRequests]);

	useEffect(() => fetchedRecommendations && setRecommendations(fetchedRecommendations), [fetchedRecommendations]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: showErrorSnackbar re-renders everythime
	useEffect(() => {
		if (error) {
			showErrorSnackbar("Oops! looks like we are unable to calculate your recommendations", {}, error);
			setRecommendations([]);
		}
	}, [error]);

	return <RecommendationsContext.Provider value={{ recommendations, setRecommendations, isLoading, error }}>{children}</RecommendationsContext.Provider>;
};
